import { filter, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

export namespace CasAuth {
  export enum Events {
    start = 'casAuthStart',
    end = 'casAuthEnd',
  }
}

export namespace IFrameSign {
  export enum Events {
    submitStart = 'IFrameSubmitStart',
    submitEnd = 'IFrameSubmitEnd',
    signStart = 'IFrameSignStart',
    signEnd = 'IFrameSignEnd',
  }
}

export namespace IFrameViewer {
  export enum Events {
    destroyViewer = 'IFrameViewerDestroy',
    destroyAllViewers = 'IFrameDestroyAllViewers',
    reload = 'IFrameViewerReload',
    supportedFeatures = 'IFrameViewerSupportedFeatures',
    redactionSaved = 'IFrameViewerRedactionSaved',
    lastPage = 'IFrameViewerLastPage',
    allModulesStarted = 'IFrameViewerAllAsyncModulesStarted',
  }

  export enum Requests {
    saveRedaction = 'SaveRedaction',
    scrollDown = 'ScrollDown',
  }
}

export enum GlobalEventType {
  PostMessage = 1,
  IframeBridge = 2,
}

export enum OldUiMessages {
  updateSession = 'updateSession',
  startInactive = 'startInactive',
  startJobResult = 'startJobResult',
  closeAllPopups = 'closeAllPopups',
  hashChange = 'hashChange',
  maintenanceBannerPreview = 'maintenanceBannerPreview',
}

export enum UserActivityMessages {
  bulkUploadActivity = 'bulkUploadActivity',
  iframeActivity = 'iframeActivity',
}

export enum ModalWindowMessages {
  modalWindowGeometryChange = 'modalWindowGeometryChange',
}

export enum DocumentMessages {
  documentsImported = 'documentsImported',
}

export enum AppThemeMessages {
  themeChanged = 'themeChanged',
}

export enum NewWindow {
  msbSignRedirection = 'msbSignRedirection',
  msbComposeRedirection = 'msbComposeRedirection',
}

interface GlobalEvent {
  key: any;
  data?: any;
}

@Injectable({
  providedIn: 'root',
})
export class GlobalEventService {
  private _eventBus: Subject<GlobalEvent>;

  constructor() {
    this._eventBus = new Subject<GlobalEvent>();
    let receiveMessage = (event) => {
      // TODO: we probably should check the origin
      if (event.origin && event.data) {
        const data = event.data;
        // event.data instanceof GlobalEvent
        if (data.type === GlobalEventType.PostMessage && data.key && data.data) {
          this.broadcast(data.key, data.data);
          return;
        }
        // Adobe Acrobat Sign "Web Message Events" arrive as a JSON STRING straight from the embedded
        // iframe (not our {type: PostMessage, ...} envelope). They fire client-side when the sender
        // sends the agreement (PAGE_LOAD/POST_SEND), a signer signs (ESIGN) or declines (REJECT), or
        // the Adobe session expires (SESSION_TIMEOUT) — independent of the channel webhook — so this
        // is how the embedded e-sign iframe gets closed on completion or expiry.
        this.handleAdobeSignWebMessage(event.origin, data);
      }
    };
    window.addEventListener('message', receiveMessage, false);
  }

  // Adobe Acrobat Sign posts its Web Message Events from several hosts: the account vanity host
  // (*.echosignsandbox.com / *.echosign.com), the API host (*.adobesignsandbox.com / *.adobesign.com),
  // and — for the DC "Request Signatures" drop-in — its CDN host (static.echocdn.com). Match on the
  // domain boundary, not a substring, so a look-alike (evil-echocdn.com.attacker.io) can't spoof a
  // completion event and close the signer's dialog. (TRL-22193)
  private static readonly ADOBE_SIGN_HOSTS = [
    'adobesign.com',
    'adobesignsandbox.com',
    'echosign.com',
    'echosignsandbox.com',
    'echocdn.com',
  ];

  private static isAdobeSignOrigin(origin: string): boolean {
    try {
      const { protocol, hostname } = new URL(origin);
      return (
        protocol === 'https:' &&
        GlobalEventService.ADOBE_SIGN_HOSTS.some((host) => hostname === host || hostname.endsWith(`.${host}`))
      );
    } catch {
      return false;
    }
  }

  private handleAdobeSignWebMessage(origin: string, raw: any): void {
    if (typeof raw !== 'string' || !GlobalEventService.isAdobeSignOrigin(origin)) {
      return;
    }
    let msg: { type?: string; pageName?: string; data?: { pageName?: string; message?: string } };
    try {
      msg = JSON.parse(raw);
    } catch {
      return;
    }
    // The DC drop-in nests pageName under .data ({type:'PAGE_LOAD',data:{pageName:'POST_SEND'}});
    // the older embed docs put it at the top level — accept either.
    const pageName = (msg && msg.data && msg.data.pageName) || (msg && msg.pageName);
    const agreementSent = msg && msg.type === 'PAGE_LOAD' && pageName === 'POST_SEND';
    const agreementSigned = msg && msg.type === 'ESIGN';
    const agreementDeclined = msg && msg.type === 'REJECT';
    // Adobe posts POST_SESSION_TIMEOUT right before redirecting the embedded view to its own login
    // page; PRE_SESSION_TIMEOUT is only the advance warning for Adobe's in-iframe dialog. (TRL-22517)
    const sessionExpired =
      msg && msg.type === 'SESSION_TIMEOUT' && msg.data && msg.data.message === 'POST_SESSION_TIMEOUT';
    if (agreementSent || agreementSigned || agreementDeclined) {
      // Reuse the existing close path: the signEnd subscriber closes the current e-sign iframe dialog.
      this.broadcast(IFrameSign.Events.signEnd, { done: true });
    } else if (sessionExpired) {
      // done:false — nothing was sent or signed; an expired compose stays in the Resume flow.
      this.broadcast(IFrameSign.Events.signEnd, { done: false, sessionExpired: true });
    }
  }

  public broadcast(key: any, data?: any) {
    // console.log('cast', key, data);
    this._eventBus.next({ key, data });
  }

  public on<T>(key: any): Observable<T> {
    return this._eventBus.asObservable().pipe(
      filter((event) => event?.key === key),
      map((event) => <T>event.data),
    );
  }

  public sendMessage(key, data, origin?, targetWindow?) {
    const w = targetWindow || window?.parent;

    if (!w) {
      return;
    }

    let dataObject = {
      type: GlobalEventType.PostMessage,
      key: key,
      data: data,
    };
    if (!origin) {
      origin = '*';
    }
    w.postMessage(dataObject, origin);
  }
}
